import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import StatCard from '../components/StatCard';
import AddToPipelineModal from '../components/AddToPipelineModal';
import { useAuth } from '../contexts/AuthContext';
import { canAcquire } from '../utils/roles';

// TEMPORARY bridge (Tim Lange / org 57): his Available deals are mirrored from a Google Sheet, so his
// org-wide "Available deals" board shows the sheet's own columns (Property/Price/Summary/ARV/COE/
// Picture Link) instead of the standard ones. Gated to this org. Matches Deals.jsx + sheet-sync.js.
const SHEET_SYNC_ORG_ID = 57;

// "Pipeline" — the shared, ORG-WIDE view. Lives at /pipeline; `/` is the personal Dashboard. Three metric boxes, all org-scoped, so every agent in the
// org sees identical totals. The per-user working list (stage tabs scoped to owner_user_id) lives on
// the separate "Deals" page (Deals.jsx). When an agent marks a deal Available, it surfaces in the
// Active Deals box here for the whole org.

// Period toggle for the org-wide boxes. Keys match backend periodBounds() in server.js.
// The Active-deal box ignores the period (it's current inventory); Sold/Closed + Fees honor it.
const PERIODS = [
  { key: 'current-month', label: 'Current month' },
  { key: 'next-month',    label: 'Next month' },
  { key: 'last-month',    label: 'Last month' },
  { key: 'ytd',           label: 'Year to date' },
  { key: 'all',           label: 'All time' },
];
function periodLabel(key) { return (PERIODS.find(p => p.key === key) || {}).label || ''; }

// Bed/bath/sqft/lot one-liner for the Active-deals table.
function detailStr(r) {
  const parts = [
    r.beds != null && `${r.beds} bd`,
    r.baths != null && `${r.baths} ba`,
    r.sqft && `${Number(r.sqft).toLocaleString()} sqft`,
    r.lot_size && `${Number(r.lot_size).toLocaleString()} lot`,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

function fmtMoney(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000)    return `$${Math.round(n / 1000)}k`;
  return `$${n}`;
}

function fmtDate(d) {
  if (!d) return '—';
  const s = String(d).split('T')[0].split(' ')[0];
  if (!s || s === 'null') return '—';
  const [y, m, day] = s.split('-');
  return (y && m && day) ? `${m}/${day}/${y}` : s;
}

export default function Pipeline() {
  const [stats, setStats]   = useState({});
  // Org-wide metric boxes: selected period + which box's drill-down list is open.
  const [period, setPeriod] = useState('current-month');
  const [openBox, setOpenBox]           = useState('active'); // 'active' | 'sold-closed' | null — open on Available by default
  const [soldMetric, setSoldMetric]     = useState('units'); // Sold/Closed box: 'units' | 'volume'
  const [drillRows, setDrillRows]       = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [showAdd, setShowAdd]           = useState(false);  // "Add to Pipeline" picker open?
  const [bump, setBump]                 = useState(0);      // forces a drill-list refetch after a change
  const navigate = useNavigate();
  const { org, role } = useAuth();
  // Adding a deal is acquisitions work; a sales agent's Pipeline is for selling what is on it.
  const mayAdd = canAcquire(role);
  const isSheetSyncOrg = org?.id === SHEET_SYNC_ORG_ID;   // sheet-mirror columns on the Available board

  // Org-wide box stats, period-aware. Re-pulled whenever the period changes.
  const fetchStats = useCallback((p) => {
    fetch(`/api/stats?period=${p}`).then(r => r.json()).then(setStats).catch(() => {});
  }, []);
  useEffect(() => { fetchStats(period); }, [period, fetchStats]);

  // Drill-down list under the boxes — refetches when the open box or the period changes.
  useEffect(() => {
    if (!openBox) return;
    let cancelled = false;
    setDrillLoading(true);
    const url = openBox === 'active' ? '/api/stats/active-deals' : `/api/stats/sold-closed?period=${period}`;
    fetch(url).then(r => r.json())
      .then(d => { if (!cancelled) { setDrillRows(Array.isArray(d) ? d : []); setDrillLoading(false); } })
      .catch(() => { if (!cancelled) setDrillLoading(false); });
    return () => { cancelled = true; };
  }, [openBox, period, bump]);

  // Called after a deal is marked Available — refresh the boxes + any open drill-down.
  const refreshPipeline = useCallback(() => { fetchStats(period); setBump(b => b + 1); }, [fetchStats, period]);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--white)', letterSpacing: '-0.02em', marginBottom: 6 }}>Pipeline</h1>
          <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>Team-wide deal metrics — shared across your organization.</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
          {mayAdd && <button onClick={() => setShowAdd(true)}
            style={{ padding: '9px 16px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-300)', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            + Add Deal
          </button>}
          <button onClick={() => navigate(mayAdd ? '/deals' : '/my-sales')}
            style={{ padding: '9px 16px', borderRadius: 8, background: 'var(--teal)', border: '1px solid var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {mayAdd ? 'My Deals →' : 'My Sales →'}
          </button>
        </div>
      </div>

      {/* Period toggle — drives the Sold/Closed + Assignment-Fees boxes (Active is current inventory). */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 4 }}>Period</span>
        {PERIODS.map(p => (
          <button key={p.key} onClick={() => setPeriod(p.key)}
            style={{
              padding: '5px 12px', borderRadius: 20, border: '1px solid var(--navy-border)', cursor: 'pointer',
              fontSize: 12, fontWeight: 600,
              background: period === p.key ? 'var(--teal)' : 'transparent',
              color: period === p.key ? '#fff' : 'var(--gray-500)',
            }}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Org-wide metric boxes — same totals for every agent in the org. Click to drill in. */}
      <div style={{ display: 'flex', gap: 16, marginBottom: openBox ? 16 : 28, flexWrap: 'wrap' }}>
        <StatCard label="Active Deals" value={(stats.activeDeals ?? 0).toLocaleString()}
          sub="Available now · org-wide" accent="teal" icon={<HomeIcon />}
          onClick={() => setOpenBox(b => b === 'active' ? null : 'active')} active={openBox === 'active'} />
        {/* Sold/Closed box — toggles between unit count and total sold-price volume. The toggle
            sits in the card corner and stops propagation so it doesn't open the drill-down. */}
        <div style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex' }}>
          <StatCard
            label="Sold/Closed Deals"
            value={soldMetric === 'volume' ? fmtMoney(stats.soldClosedVolume) : (stats.soldClosedDeals ?? 0).toLocaleString()}
            sub={`${soldMetric === 'volume' ? 'Volume' : 'Units'} · ${periodLabel(period)}`}
            accent="gold"
            onClick={() => setOpenBox(b => b === 'sold-closed' ? null : 'sold-closed')} active={openBox === 'sold-closed'} />
          {/* Units/$ toggle sits where the icon used to be, level with the box label. */}
          <div onClick={e => e.stopPropagation()}
            style={{ position: 'absolute', top: 18, right: 20, display: 'flex', gap: 2, background: 'var(--navy)', border: '1px solid var(--navy-border)', borderRadius: 8, padding: 2 }}>
            {[{ k: 'units', t: 'Units' }, { k: 'volume', t: '$ Vol' }].map(o => (
              <button key={o.k} onClick={() => setSoldMetric(o.k)}
                style={{
                  padding: '3px 9px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700,
                  background: soldMetric === o.k ? 'var(--gold)' : 'transparent',
                  color: soldMetric === o.k ? '#fff' : 'var(--gray-500)',
                }}>
                {o.t}
              </button>
            ))}
          </div>
        </div>
        <StatCard label="Assignment Fees" value={fmtMoney(stats.assignmentFees)}
          sub={`Sold + Closed · ${periodLabel(period)}`} accent="gold" icon={<DollarIcon />}
          onClick={() => setOpenBox(b => b === 'sold-closed' ? null : 'sold-closed')} active={openBox === 'sold-closed'} />
      </div>

      {/* Drill-down list for whichever box is open. */}
      {openBox && (
        <DrillPanel box={openBox} rows={drillRows} loading={drillLoading} period={period} onChanged={refreshPipeline}
          sheetMirror={isSheetSyncOrg}
          onClose={() => setOpenBox(null)} onAdd={mayAdd ? () => setShowAdd(true) : null} navigate={navigate} />
      )}

      {/* "Add to Pipeline" picker — mark one of your deals Available so it shows in the Active box. */}
      {showAdd && (
        <AddToPipelineModal onClose={() => setShowAdd(false)} onChanged={refreshPipeline} />
      )}
    </div>
  );
}

// Drill-down list shown under a clicked metric box. 'active' → org-wide Available inventory;
// 'sold-closed' → org-wide Sold+Closed for the selected period. Rows link to the deal detail (edit);
// the header "+ Add Deal" opens the existing Add Deal form (add).
function DrillPanel({ box, rows, loading, period, onClose, onAdd, navigate, sheetMirror, onChanged }) {
  const isActive = box === 'active';
  // Claim = "I am the acquisitions agent on this one." Moves the deal onto the claimer's Deals tab
  // and gives them its financials. The backend decides eligibility (routes/deals.js POST /:id/claim);
  // `claimable` on the row is only what the UI renders from, never the authority.
  const [claiming, setClaiming] = React.useState(null);
  const [claimErr, setClaimErr] = React.useState(null);
  const claim = async (e, dealId) => {
    e.stopPropagation();                     // the row itself navigates; the button must not
    setClaiming(dealId); setClaimErr(null);
    try {
      const r = await fetch(`/api/deals/${dealId}/claim`, { method: 'POST', credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      // 409 = someone else got there first, or it already moved on. Show what happened and refresh
      // so the row updates to reality rather than silently doing nothing.
      if (!r.ok) setClaimErr(d.error || 'Could not claim this deal.');
      onChanged && onChanged();
    } catch {
      setClaimErr('Could not claim this deal.');
    } finally { setClaiming(null); }
  };
  // Sheet-bridge (org 57): the Available board mirrors the Google Sheet's columns.
  const sheet = isActive && sheetMirror;
  const title = isActive ? 'Available deals · org-wide' : `Sold + Closed · ${periodLabel(period)}`;
  return (
    <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, marginBottom: 28, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderBottom: '1px solid var(--navy-border)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)' }}>
          {title} <span style={{ color: 'var(--gray-500)', fontWeight: 500 }}>({rows.length})</span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {onAdd && <button onClick={onAdd} style={{ padding: '5px 12px', borderRadius: 6, background: 'var(--teal)', border: 'none', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>+ Add Deal</button>}
          <button onClick={onClose} style={{ padding: '5px 10px', borderRadius: 6, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-300)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Close</button>
        </div>
      </div>
      {claimErr && (
        <div style={{ padding: '9px 18px', fontSize: 12.5, color: 'var(--gold)', borderBottom: '1px solid var(--navy-border)', background: 'rgba(224,165,38,0.10)' }}>
          {claimErr}
        </div>
      )}
      {loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
          {isActive ? 'No Available deals. Mark a deal Available to surface it here.' : `No Sold/Closed deals in ${periodLabel(period)}.`}
        </div>
      ) : (
        // High-level table. Active (Available) and Sold/Closed share the same columns; Sold/Closed
        // adds a Fee column (per-deal fee on completed deals — the Assignment Fees box already sums
        // these org-wide, so it's intentionally visible here).
        <div style={{ overflowX: 'auto', maxHeight: 440, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isActive ? 800 : 900 }}>
            <thead>
              <tr style={{ background: 'rgba(250, 247, 242,0.6)', borderBottom: '1px solid var(--navy-border)' }}>
                {(sheet
                  ? ['Property', 'Price', 'Summary', 'ARV', 'COE', 'Picture Link', 'Acquisitions Agent']
                  : ['Property', 'Details', 'Year Built', 'Price', 'Close Date', 'Title Company',
                     ...(isActive ? ['Acquisitions Agent'] : ['Fee'])]
                ).map(h => (
                  <th key={h} style={{ padding: '9px 16px', fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: h === 'Fee' ? 'right' : 'left', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} onClick={() => navigate(`/pipeline/deal/${r.id}`)}
                  style={{ borderBottom: i === rows.length - 1 ? 'none' : '1px solid rgba(232,227,218,0.5)', cursor: 'pointer' }}
                  onMouseOver={e => e.currentTarget.style.background = 'rgba(20, 80, 74,0.04)'}
                  onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '10px 16px' }}>
                    <div style={{ fontSize: 13, color: 'var(--white)', fontWeight: 600 }}>{r.address}</div>
                    <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{[r.city, r.state, r.zip].filter(Boolean).join(', ')}</div>
                  </td>
                  {sheet ? (
                    <>
                      <td style={{ padding: '10px 16px', fontSize: 13, color: 'var(--white)', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtMoney(r.asking_price)}</td>
                      <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--gray-300)', maxWidth: 340 }}>{r.description || '—'}</td>
                      <td style={{ padding: '10px 16px', fontSize: 13, color: 'var(--white)', whiteSpace: 'nowrap' }}>{r.arv_text || (r.ai_arv ? fmtMoney(r.ai_arv) : '—')}</td>
                      <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--gray-300)', whiteSpace: 'nowrap' }}>{r.close_of_escrow || (r.close_date ? fmtDate(r.close_date) : '—')}</td>
                      <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        {r.photos_link
                          ? <a href={r.photos_link} target="_blank" rel="noreferrer" style={{ color: 'var(--teal)', fontSize: 12, textDecoration: 'underline' }}>View photos ↗</a>
                          : <span style={{ color: 'var(--gray-500)', fontSize: 12 }}>—</span>}
                      </td>
                  {isActive && (
                    <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                      {r.acquisitions_agent_name ? (
                        <span style={{ fontSize: 12.5, color: 'var(--white)', fontWeight: 600 }}>{r.acquisitions_agent_name}</span>
                      ) : r.claimable ? (
                        <button onClick={e => claim(e, r.id)} disabled={claiming === r.id}
                          style={{ padding: '4px 11px', borderRadius: 6, border: '1px solid var(--teal)',
                                   background: claiming === r.id ? 'transparent' : 'var(--teal)',
                                   color: claiming === r.id ? 'var(--teal)' : '#fff',
                                   fontSize: 11.5, fontWeight: 700, cursor: claiming === r.id ? 'default' : 'pointer' }}>
                          {claiming === r.id ? 'Claiming…' : 'Claim'}
                        </button>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>— unclaimed</span>
                      )}
                    </td>
                  )}
                    </>
                  ) : (
                    <>
                      <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--gray-300)', whiteSpace: 'nowrap' }}>{detailStr(r)}</td>
                      {/* Year built as its own column rather than folded into Details — it is what an
                          agent scans the board for, and inside a "3 bd · 2 ba · …" run it disappears. */}
                      <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--gray-300)', whiteSpace: 'nowrap' }}>{r.year_built || '—'}</td>
                      <td style={{ padding: '10px 16px', fontSize: 13, color: 'var(--white)', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtMoney(isActive ? r.asking_price : (r.sale_price || r.asking_price))}</td>
                      <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--gray-300)', whiteSpace: 'nowrap' }}>{fmtDate(r.close_date || r.close_of_escrow)}</td>
                      <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--gray-300)', whiteSpace: 'nowrap' }}>{r.title_company || '—'}</td>
                      {!isActive && <td style={{ padding: '10px 16px', fontSize: 13, color: 'var(--gold)', fontWeight: 600, whiteSpace: 'nowrap', textAlign: 'right' }}>{fmtMoney(r.fee)}</td>}
                  {isActive && (
                    <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                      {r.acquisitions_agent_name ? (
                        <span style={{ fontSize: 12.5, color: 'var(--white)', fontWeight: 600 }}>{r.acquisitions_agent_name}</span>
                      ) : r.claimable ? (
                        <button onClick={e => claim(e, r.id)} disabled={claiming === r.id}
                          style={{ padding: '4px 11px', borderRadius: 6, border: '1px solid var(--teal)',
                                   background: claiming === r.id ? 'transparent' : 'var(--teal)',
                                   color: claiming === r.id ? 'var(--teal)' : '#fff',
                                   fontSize: 11.5, fontWeight: 700, cursor: claiming === r.id ? 'default' : 'pointer' }}>
                          {claiming === r.id ? 'Claiming…' : 'Claim'}
                        </button>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>— unclaimed</span>
                      )}
                    </td>
                  )}
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function HomeIcon()       { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>; }
function DollarIcon()     { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>; }
